import { z } from "zod";

const GatewayForbiddenSchema = z.object({ message: z.literal("Forbidden") }).strict();
let session: { token: string | undefined; expiresAt: number | undefined } | undefined;
let rejectedToken: string | undefined;
const listeners = new Set<() => void>();

/** In-memory credential and expiry metadata. Register before child queries start. */
export function setApiSession(token: string | undefined, expiresAt?: number): void {
  if (session?.token !== token) rejectedToken = undefined;
  session = { token, expiresAt };
}

export function sessionNeedsSignIn(): boolean {
  return rejectedToken !== undefined && session?.token === rejectedToken;
}

export function subscribeSessionRejection(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export class SessionExpiredError extends Error {
  constructor() {
    super("Your session has ended. Sign in again to continue.");
    this.name = "SessionExpiredError";
  }
}

function rejectSession(token: string): never {
  if (rejectedToken !== token) {
    rejectedToken = token;
    for (const listener of listeners) listener();
  }
  throw new SessionExpiredError();
}

/** Used only by AIRE API clients; never patches browser fetch or retries a mutation. */
export async function sessionFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<Response> {
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  );
  const authorization = headers.get("Authorization");
  const token = authorization?.startsWith("Bearer ") ? authorization.slice(7) : undefined;
  const isCurrent = (): boolean => token !== undefined && session?.token === token;
  if (token && isCurrent()) {
    if (
      rejectedToken === token ||
      (session?.expiresAt !== undefined && session.expiresAt * 1000 <= Date.now())
    ) {
      rejectSession(token);
    }
  }
  const response = await fetchImpl(input, init);
  if (!token || !isCurrent() || init?.signal?.aborted) return response;
  // The shared Lambda authorizer returns a bare gateway 403 for rejected credentials.
  // Application permission errors have their own structured payload and must remain errors.
  if (response.status === 401) rejectSession(token);
  if (response.status === 403) {
    const payload: unknown = await response
      .clone()
      .json()
      .catch(() => undefined);
    if (isCurrent() && GatewayForbiddenSchema.safeParse(payload).success) rejectSession(token);
  }
  return response;
}
